import { routes } from "../apiroutes";
import HttpService from "../httpServices";
import useMutateItem from "../useMutateItem";
// import { getAccessToken, parseData, stringifyData } from "../../utils";
import { ErrorHandler, extractApiErrorDetail } from "../errorHandler";
import { showErrorAlert, showSuccessAlert } from "../../utils/alert";
import useFetchItem from "../useFetchItem";

const httpService = new HttpService();


export const useGetEmployees = ({ enabled = true }) => {
    const { isFetched, isLoading, error, data, refetch, isFetching, setFilter,  } = useFetchItem({
        queryKey: ["fetchEmployees"],
        queryFn: () => {
          return httpService.getData(routes.getEmployees(),)},
        enabled,
        retry: 2
    });

    return {
        getEmployeesIsFetching: isFetching,
        getEmployeesIsFetched: isFetched,
        getEmployeesIsLoading: isLoading,
        getEmployeesError: ErrorHandler(error),
        getEmployeesData: data?.data || {},
        refetchGetEmployees: refetch,
        getEmployeesSetFilter: setFilter
    };
};

export const useGetEmployee = ({ employeeId, enabled = true }) => {
    const { isFetched, isLoading, error, data, refetch, isFetching } = useFetchItem({
        queryKey: ["fetchEmployee", employeeId],
        queryFn: () => {
          return httpService.getData(routes.getEmployee(employeeId),)},
        enabled: enabled && !!employeeId,
        retry: 2
    });

    return {
        getEmployeeIsFetching: isFetching,
        getEmployeeIsFetched: isFetched,
        getEmployeeIsLoading: isLoading,
        getEmployeeError: ErrorHandler(error),
        getEmployeeData: data?.data || {},
        refetchGetEmployee: refetch
    };
};

export const useGetEmployeesByFliter = ({ enabled = false }) => {
    const { isFetched, isLoading, error, data, refetch, isFetching, setFilter,  } = useFetchItem({
        queryKey: ["fetchEmployeesByFilter"],
        queryFn: (queryParams) => {
          console.log(queryParams);
          return httpService.getData(routes.getEmployeesByFilter(queryParams),)},
        enabled,
        retry: 2
    });

    return {
        filterEmployeesIsFetching: isFetching,
        filterEmployeesIsFetched: isFetched,
        filterEmployeesIsLoading: isLoading,
        filterEmployeesError: ErrorHandler(error),
        filterEmployeesData: data?.data || {},
        refetchFilterEmployees: refetch,
        filterEmployeesSetFilter: setFilter
    };
};

export const useGetEmployeeOnboardingDocuments = ({ employeeId, enabled = true }) => {
  const { isFetched, isLoading, error, data, refetch, isFetching } = useFetchItem({
    queryKey: ["employeeOnboardingDocuments", employeeId],
    queryFn: () => httpService.getData(routes.employeeOnboardingDocuments(employeeId)),
    enabled: enabled && !!employeeId,
    retry: 1,
  });

  return {
    onboardingDocsIsFetching: isFetching,
    onboardingDocsIsFetched: isFetched,
    onboardingDocsIsLoading: isLoading,
    onboardingDocsError: ErrorHandler(error),
    onboardingDocsData: data?.data || {},
    refetchOnboardingDocs: refetch,
  };
};

/** Download onboarding file (triggers browser save). */
export const downloadEmployeeOnboardingDocument = async (employeeId, documentId) => {
  const svc = new HttpService();
  const res = await svc.getBlob(
    routes.employeeOnboardingDocumentFile(employeeId, documentId),
  );
  const blob = res.data;
  const disposition = res.headers["content-disposition"] || "";
  let filename = "document";
  const quoted = /filename="([^"]+)"/i.exec(disposition);
  const unquoted = /filename=([^;\s]+)/i.exec(disposition);
  if (quoted?.[1]) filename = quoted[1];
  else if (unquoted?.[1]) filename = unquoted[1].replace(/^["']|["']$/g, "");

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const useAddEmployees = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (payload) =>
      httpService.postData(payload, routes.addEmployee()),
    onSuccess: (res) => {
      const resData = res?.data || {};
      showSuccessAlert(resData?.message);
      handleSuccess(resData);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    addEmployeeData: data,
    addEmployeeError: ErrorHandler(error),
    addEmployeeIsLoading: isPending,
    addEmployeePayload: (requestPayload) => mutate(requestPayload),
  };
};


export const useEditEmployee = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (payload) =>
      httpService.putData(payload, routes.editEmployee(payload?.employee_id)),
    onSuccess: (res) => {
      const resData = res?.data || {};
      showSuccessAlert(resData?.message);
      handleSuccess(resData);
    },
    onError: (err) => {
      console.log(err);
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    editEmployeeData: data,
    editEmployeeError: ErrorHandler(error),
    editEmployeeIsLoading: isPending,
    editEmployeePayload: (requestPayload) => mutate(requestPayload),
  };
};

export const useDeleteEmployee = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (id) =>
      httpService.deleteData(routes.deleteEmployee(id)),
    onSuccess: (res) => {
      const resData = res?.data || {};
      showSuccessAlert(resData?.message);
      handleSuccess(resData);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    deleteEmployeeData: data,
    deleteEmployeeError: ErrorHandler(error),
    deleteEmployeeIsLoading: isPending,
    deleteEmployeePayload: (requestPayload) => mutate(requestPayload),
  };
};

// Get archived employees (Admin only)
export const useGetArchivedEmployees = ({ enabled = true, branch_id = null, page = 1, limit = 20 }) => {
  const { isFetched, isLoading, error, data, refetch, isFetching } = useFetchItem({
    queryKey: ["fetchArchivedEmployees", branch_id, page, limit],
    queryFn: () => {
      const params = { page, limit };
      if (branch_id && branch_id !== 'all') {
        params.branch_id = branch_id;
      }
      return httpService.getData(routes.getArchivedEmployees(params));
    },
    enabled,
    retry: 2
  });

  return {
    archivedEmployeesIsFetching: isFetching,
    archivedEmployeesIsFetched: isFetched,
    archivedEmployeesIsLoading: isLoading,
    archivedEmployeesError: ErrorHandler(error),
    archivedEmployeesData: data?.data || {},
    refetchArchivedEmployees: refetch
  };
};

// Restore archived employee
export const useRestoreEmployee = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (employeeId) =>
      httpService.putData({}, routes.restoreEmployee(employeeId)),
    onSuccess: (res) => {
      const resData = res?.data || {};
      showSuccessAlert(resData?.message || "Employee restored successfully");
      handleSuccess(resData);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    restoreEmployeeData: data,
    restoreEmployeeError: ErrorHandler(error),
    restoreEmployeeIsLoading: isPending,
    restoreEmployeePayload: (employeeId) => mutate(employeeId),
  };
};


export const useUploadProfileImg = (handleSuccess) => {
  const { data, error, isPending, mutateAsync } = useMutateItem({
    mutationFn: (data) =>{
        const {cloudName, payload} = data
     return httpService.postDataWithoutToken(payload, routes.uploadProfileImg(cloudName), "CLOUDINARY")},
    onSuccess: (res) => {
      const resData = res?.data || {};
      showSuccessAlert("Image uploaded successfully");
      handleSuccess(resData);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    uploadData: data,
    uploadError: ErrorHandler(error),
    uploadIsLoading: isPending,
    uploadPayload: (requestPayload) => mutateAsync(requestPayload),
  };
};


// Regular payroll (for branch users)
export const useGetPayroll = ({ enabled = false }) => {
  const { isFetched, isLoading, error, data, refetch, isFetching, setFilter,  } = useFetchItem({
      queryKey: ["fetchEmployeesPayroll"],
      queryFn: (queryParams) => {
        console.log(queryParams);
        return httpService.getData(routes.getPayroll(queryParams),)},
      enabled,
      retry: 2
  });

  return {
      payrollIsFetching: isFetching,
      payrollIsFetched: isFetched,
      payrollIsLoading: isLoading,
      payrollError: ErrorHandler(error),
      payrollData: data?.data?.payroll_entries || data?.data || [],
      refetchPayroll: refetch,
      payrollSetFilter: setFilter
  };
};

// Admin payroll (for company admins - cross-branch access)
export const useGetAdminPayroll = ({ enabled = false, branch_id = null }) => {
  const { isFetched, isLoading, error, data, refetch, isFetching, setFilter } = useFetchItem({
    queryKey: ["fetchAdminPayroll", branch_id],
    queryFn: (queryParams) => {
      const params = { ...queryParams };
      if (branch_id && branch_id !== 'all') {
        params.branch_id = branch_id;
      }
      console.log('Admin Payroll Query Params:', params);
      return httpService.getData(routes.getPayroll(params),);
    },
    enabled,
    retry: 2
  });

  return {
    adminPayrollIsFetching: isFetching,
    adminPayrollIsFetched: isFetched,
    adminPayrollIsLoading: isLoading,
    adminPayrollError: ErrorHandler(error),
    adminPayrollData: data?.data?.payroll_entries || data?.data || [],
    refetchAdminPayroll: refetch,
    adminPayrollSetFilter: setFilter
  };
};


export const useGetLeave = ({ enabled = false }) => {
  const { isFetched, isLoading, error, data, refetch, isFetching, setFilter,  } = useFetchItem({
      queryKey: ["fetchEmployeesLeave"],
      queryFn: (queryParams) => {
        // console.log(queryParams);
        return httpService.getData(routes.getLeave(),)},
      enabled,
      retry: 2
  });

  return {
      leaveIsFetching: isFetching,
      leaveIsFetched: isFetched,
      leaveIsLoading: isLoading,
      leaveError: ErrorHandler(error),
      leaveData: data?.data || [],
      refetchLeave: refetch,
      leaveSetFilter: setFilter
  };
};


export const useApproveLeave = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (payload) =>
      httpService.postData(payload, routes.approveLeave()),
    onSuccess: (res) => {
      const resData = res?.data || {};
      showSuccessAlert(resData?.message);
      handleSuccess(resData);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    approveLeaveData: data,
    approveLeaveError: ErrorHandler(error),
    approveLeaveIsLoading: isPending,
    approveLeavePayload: (requestPayload) => mutate(requestPayload),
  };
};

export const useRecallLeave = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (payload) =>
      httpService.postData(payload, routes.recallLeave()),
    onSuccess: (res) => {
      const resData = res?.data || {};
      showSuccessAlert(resData?.message);
      handleSuccess(resData);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    recallLeaveData: data,
    recallLeaveError: ErrorHandler(error),
    recallLeaveIsLoading: isPending,
    recallLeavePayload: (requestPayload) => mutate(requestPayload),
  };
};

export const useLeaveRemind = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (leaveId) =>
      httpService.postData({}, routes.leaveRequestRemind(leaveId)),
    queryKeys: ["fetchEmployeesLeave"],
    onSuccess: (res) => {
      const resData = res?.data || {};
      showSuccessAlert(resData?.message || "Reminder sent");
      handleSuccess(resData);
    },
    onError: (err) => {
      const extracted = extractApiErrorDetail(err);
      showErrorAlert(
        extracted.message || "Could not send reminder. Check that the request is still pending.",
      );
    },
  });

  return {
    leaveRemindData: data,
    leaveRemindError: ErrorHandler(error),
    leaveRemindIsLoading: isPending,
    leaveRemindPayload: (leaveId) => mutate(leaveId),
  };
};

// Bulk Upload Hooks
export const useGetBulkUploadTemplate = ({ enabled = false }) => {
  const { isFetched, isLoading, error, data, refetch, isFetching } = useFetchItem({
    queryKey: ["bulkUploadTemplate"],
    queryFn: () => httpService.getData(routes.bulkUploadTemplate()),
    enabled,
    retry: 2
  });

  return {
    bulkUploadTemplateIsFetching: isFetching,
    bulkUploadTemplateIsFetched: isFetched,
    bulkUploadTemplateIsLoading: isLoading,
    bulkUploadTemplateError: ErrorHandler(error),
    bulkUploadTemplateData: data?.data || {},
    refetchBulkUploadTemplate: refetch
  };
};

// Direct CSV download function
export const downloadBulkUploadTemplate = async () => {
  try {
    const response = await httpService.getData(routes.bulkUploadTemplate(true));
    
    // Create blob from response data
    const blob = new Blob([response.data], { type: 'text/csv' });
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'bulk_upload_template.csv';
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return { success: true };
  } catch (error) {
    console.error('Error downloading template:', error);
    throw error;
  }
};

export const useBulkUploadEmployees = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (formData) =>
      httpService.postFormData(formData, routes.bulkUpload()),
    onSuccess: (res) => {
      const resData = res?.data || {};
      showSuccessAlert(resData?.message || "Bulk upload completed successfully!");
      handleSuccess(resData);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    bulkUploadData: data,
    bulkUploadError: ErrorHandler(error),
    bulkUploadIsLoading: isPending,
    bulkUploadPayload: (formData) => mutate(formData),
  };
};

